import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Dictionary_Service } from '../../services/dictionary.service';
import { IdentitySubscription_Service } from '../../services';

@Component({
    selector: 'subscription-form',
    templateUrl: './subscription-form.component.html',
    styleUrls: ['./subscription-form.component.scss']
})
export class SubscriptionFormComponent implements OnInit, OnChanges {
    constructor(
        public dictionary: Dictionary_Service,
        public subscriptionService: IdentitySubscription_Service
    ) { }
 
    // changes the display depending on the route the form is used in.
    @Input() 
    public showWide: boolean = false;

    /**  
     * flag for displaying the input field and "Add Subscribers" button.
     * 
     * DEV NOTE: this will always be "true" for the "order/create" route, 
     * because we will always be in "edit mode" on that route
     * */
    @Input()
    public showEdit: boolean = true;

    /** 
     * Flag for checking whether the subscriber changes are saved.
     * Used in combination with the UnsavedChangesGuard 
     * and the "beforeunload" Event (see "order/view" route),
     * to check for and warn the user of unsaved changes.
     * 
     * DEV NOTE: this is not used for the "order/create" route, 
     * because subscribers are not saved until the order has been processed, 
     * though the events may still be emitted.
     * */ 
    @Input()
    public unsavedSubscribers: boolean = false;

    @Output()
    unsavedSubscribersChange: EventEmitter<boolean> = new EventEmitter();

    /** 
     * string list of email addresses for subscribers.
     * 
     * DEV NOTE: this is used for all routes to pass data from the route to the form component and back
     * */ 
    @Input()
    public subscriptions: string[] = [];

    @Output()
    public subscriptionsChange: EventEmitter<string[]> = new EventEmitter();
    
    // allow the placeholder to be modified
    @Input()
    public placeholder: string = "Subscriber Emails (Optional)";
    
    public subscriptionEmails = new FormControl({value:'', disabled: !this.showEdit});

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges) {
        // update the form when the showEdit flag is changed
        if(changes['showEdit']) {
            if(changes['showEdit'].currentValue) 
                this.subscriptionEmails.enable();
            else
                this.subscriptionEmails.disable();
        }
    }

    public removeSubscription(index: number) : void {
        this.subscriptions.splice(index, 1);
        this.unsavedSubscribers = true;
        
        this.subscriptionsChange.emit(this.subscriptions);
        this.unsavedSubscribersChange.emit(this.unsavedSubscribers);
    }

    public addSubscriptions() : void {
        const subscriptionControl = this.subscriptionEmails;

        // check the value for a string with at least one character
        if(!subscriptionControl?.value.trim()) return;

        const DOMAIN_REGEX = /^[^@\.][^@]*@[^@-][^@]*[^@-]*$/gm;

        // split the new emails
        let emails = subscriptionControl.value.split(',');
        // trim the addresses, then check for missing domains or whitespaces and remove those emails as invalid 
        let sanitizedEmails: Array<string> = emails
            .map((e:string) => e.trim())
            .filter((e:string) => { return (0 == e.search(DOMAIN_REGEX) && -1 == e.search(/\s/gm)); });

        // join the existing subscriptions and the incoming subscriptions into a single list 
        let newSubscriptions: Array<string> = [...this.subscriptions, ...sanitizedEmails];
        
        // sort so we can remove dupes.
        newSubscriptions.sort();

        // dedupe, and set the local variable 
        this.subscriptions = newSubscriptions.filter((s, i) => (s != newSubscriptions[i+1]));
        this.unsavedSubscribers = true;
        
        // emit the updates on the two way binding
        this.subscriptionsChange.emit(this.subscriptions);
        this.unsavedSubscribersChange.emit(this.unsavedSubscribers);

        // clear the form after emails are added
        this.subscriptionEmails.reset('');
    }
}
